/**
 * Phase 3 — Loader de traducciones dinámico
 * Carga cada idioma bajo demanda via dynamic import.
 * Fallback seguro a inglés (en) para evitar runtime roto.
 * @file loader.js
 */

import { DEFAULT_LANGUAGE } from './supportedLanguages.js';
import { SUPPORTED_LANGUAGES } from './supportedLanguages.js';
import en from './locales/en.js';
import es from './locales/es.js';
import pt from './locales/pt.js';
import fr from './locales/fr.js';
import it from './locales/it.js';
import de from './locales/de.js';
import nl from './locales/nl.js';
import pl from './locales/pl.js';
import ru from './locales/ru.js';
import el from './locales/el.js';
import tr from './locales/tr.js';
import ar from './locales/ar.js';
import he from './locales/he.js';
import hi from './locales/hi.js';
import ja from './locales/ja.js';
import ko from './locales/ko.js';
import zh from './locales/zh.js';
import sv from './locales/sv.js';

/** Cache módulo: persistente durante toda la sesión. Sobrevive al cambio de idioma. */
const translationCache = {};
const translationsMap = {
  en,
  es,
  pt,
  fr,
  it,
  de,
  nl,
  pl,
  ru,
  el,
  tr,
  ar,
  he,
  hi,
  ja,
  ko,
  zh,
  sv,
};

export const getCachedLanguages = () => Object.keys(translationCache);
export const getTranslationsMap = () => translationsMap;

/**
 * Carga las traducciones de un idioma vía dynamic import.
 * Cachea tras primera carga; nunca re-importa.
 * @param {string} lang - Código de idioma (es, en, pt, fr, etc.)
 * @returns {Promise<Object>} Objeto de traducciones
 */
export const loadTranslations = async (lang) => {
  const normalizedLang = (lang?.toLowerCase() || DEFAULT_LANGUAGE).trim();
  const supported = SUPPORTED_LANGUAGES || [];
  const targetLang = supported.includes(normalizedLang) ? normalizedLang : DEFAULT_LANGUAGE;

  if (translationCache[targetLang] && typeof translationCache[targetLang] === 'object') {
    return translationCache[targetLang];
  }

  const enPack = translationsMap.en && typeof translationsMap.en === 'object' ? translationsMap.en : {};
  const targetPack = translationsMap[targetLang] && typeof translationsMap[targetLang] === 'object'
    ? translationsMap[targetLang]
    : null;
  if (!targetPack) {
    console.warn(`[I18N] Missing locale map for ${targetLang}, falling back to en`);
  }
  translationCache.en = enPack;
  translationCache[targetLang] = targetPack || enPack;
  return translationCache[targetLang];
};

/**
 * Obtiene traducciones del caché (sync).
 * Retorna null si no están cargadas.
 */
export const getTranslationsSync = (lang) => {
  const normalizedLang = (lang?.toLowerCase() || DEFAULT_LANGUAGE).trim();
  const cached = translationCache[normalizedLang];
  return cached && typeof cached === 'object' ? cached : null;
};

/**
 * Limpia el caché.
 */
export const clearTranslationsCache = () => {
  Object.keys(translationCache).forEach((k) => delete translationCache[k]);
};

/**
 * Invalida caché de un idioma.
 */
export const invalidateCache = (lang) => {
  const normalizedLang = (lang?.toLowerCase() || DEFAULT_LANGUAGE).trim();
  if (translationCache[normalizedLang]) {
    delete translationCache[normalizedLang];
  }
};
